import React, { useEffect, useState, useMemo, useCallback } from 'react';
import apiFetch from '../../../utils/apiFetch';
import { Modal, Card, Button, Form, Tabs, Tab, Badge } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import UpcastModal from './UpcastModal';
import DockControls from '../components/DockControls';
import { getClassCastingProfile } from '../../../shared/rules/classSpellcasting.mjs';
import { isBardSpellFor, magicalSecretsLabel } from '../../../shared/rules/bard.mjs';
import { getClassSpellLimits, spellChangesLeft } from '../../../shared/rules/spellPreparation';
import { getSpellbook, ritualGrantId } from '../../../shared/rules/wizard.mjs';

/** What this class can still replace, for the Spells page; '' when nothing limits it yet. */
export const spellChangeNote = (form, className) => {
  const id = String(className || '').trim().toLowerCase();
  const left = (kind, what) => {
    const count = spellChangesLeft(form, id, kind);
    if (count === null) return '';
    if (count === 'any') return `Change any ${what}s until you spend a spell slot`;
    return count ? `${count} ${what} change${count === 1 ? '' : 's'} left` : `No ${what} changes left`;
  };
  return [left('spells', 'spell'), left('cantrips', 'cantrip')].filter(Boolean).join(' · ');
};

const spellSlug = (name) => String(name || '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');

/**
 * Modal component allowing users to select spells for their character.
 * Spells are fetched from the server and filtered by class and level.
 */
const SCHOOL_ICONS = {
  abjuration: 'fa-shield-halved',
  conjuration: 'fa-circle-nodes',
  divination: 'fa-eye',
  enchantment: 'fa-heart',
  evocation: 'fa-fire-flame-curved',
  illusion: 'fa-masks-theater',
  necromancy: 'fa-skull',
  transmutation: 'fa-wand-magic-sparkles',
};

const CLASS_ICONS = {
  bard: 'fa-music',
  cleric: 'fa-sun',
  druid: 'fa-leaf',
  paladin: 'fa-shield-heart',
  ranger: 'fa-feather',
  sorcerer: 'fa-bolt',
  warlock: 'fa-hand-sparkles',
  wizard: 'fa-hat-wizard',
};

const normalizeToken = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const spellIconClass = (spell) => {
  const text = `${spell?.name || ''} ${spell?.school || ''} ${spell?.description || ''}`.toLowerCase();
  if (/fire|flame|burn|scorch/.test(text)) return 'fa-fire-flame-curved';
  if (/ice|cold|frost|sleet/.test(text)) return 'fa-snowflake';
  if (/heal|cure|restore|reviv/.test(text)) return 'fa-hand-holding-heart';
  if (/poison|acid|venom/.test(text)) return 'fa-flask-vial';
  if (/lightning|thunder|storm|bolt/.test(text)) return 'fa-bolt-lightning';
  if (/charm|suggest|friend|dominat/.test(text)) return 'fa-heart';
  if (/dead|death|necrotic|zombie/.test(text)) return 'fa-skull';
  if (/invisible|illusion|mirror|phantom/.test(text)) return 'fa-masks-theater';
  return SCHOOL_ICONS[normalizeToken(spell?.school)] || 'fa-sparkles';
};

const summarizeSpell = (description = '') => {
  const clean = String(description || '').replace(/\s+/g, ' ').trim();
  if (!clean) return 'An arcane entry from the RealmTracker archives.';
  return clean.length > 150 ? `${clean.slice(0, 147)}…` : clean;
};

const normalizeClassName = (value) =>
  typeof value === 'string' ? value.trim().toLowerCase() : '';

const spellSupportsClass = (spell, className, character) => {
  const normalized = normalizeClassName(className);
  if (!normalized) {
    return false;
  }
  if (normalized === 'bard') return isBardSpellFor(character, spell);
  const classes = Array.isArray(spell?.classes) ? spell.classes : [];
  return classes.some((cls) => normalizeClassName(cls) === normalized);
};

export default function SpellSelector({
  form,
  show,
  handleClose,
  onSpellsChange,
  onCastSpell,
  availableSlots = { regular: {}, warlock: {} },
  isDocked = false,
  dockedSide = null,
  onDockClose,
  onDockChange,
}) {
  const params = useParams();

  const totalLevel = useMemo(
    () =>
      Array.isArray(form.occupation)
        ? form.occupation.reduce(
            (total, el) => total + Number(el.Level),
            0
          )
        : 0,
    [form.occupation]
  );

  const getAvailableLevels = useCallback((level, className) => {
    const { cantrips, maxSpellLevel } = getClassSpellLimits(className, level);
    return [...(cantrips ? [0] : []), ...Array.from({ length: maxSpellLevel }, (_, index) => index + 1)];
  }, []);

  const classesInfo = useMemo(() => {
    return (form.occupation || [])
      .map((o) => {
        const name = o.Name || o.Occupation;
        const level = Number(o.Level) || 0;
        const key = (name || '').toLowerCase();
        const casterProgression = getClassCastingProfile(key)?.casterProgression || 'none';
        let effectiveLevel = 0;
        if (casterProgression === 'full' || casterProgression === 'pact') {
          effectiveLevel = level;
        } else if (casterProgression === 'half') {
          effectiveLevel = Math.ceil(level / 2);
        }
        return { name, level, casterProgression, effectiveLevel };
      })
      .filter((o) => o.effectiveLevel >= 1);
  }, [form.occupation]);

  const levelOptions = useMemo(
    () =>
      classesInfo.reduce((acc, { name, level }) => {
        acc[name] = getAvailableLevels(level, name);
        return acc;
      }, {}),
    [classesInfo, getAvailableLevels]
  );

  const initialLevels = useMemo(
    () =>
      classesInfo.reduce((acc, { name }) => {
        const options = levelOptions[name] || [];
        const first =
          options.find((lvl) => lvl > 0) ?? options[0] ?? 0;
        acc[name] = first;
        return acc;
      }, {}),
    [classesInfo, levelOptions]
  );

  const [selectedLevels, setSelectedLevels] = useState(initialLevels);
  const [allSpells, setAllSpells] = useState({});
  const [selectedSpells, setSelectedSpells] = useState(
    (form.spells || []).map((s) => (typeof s === 'string' ? s : s.name))
  );
  // Track which class or caster each selected spell belongs to so it can be
  // persisted along with the spell. This is needed for grouping in
  // PlayerTurnActions.
  const [spellCasters, setSpellCasters] = useState(
    (form.spells || []).reduce((acc, s) => {
      if (s && typeof s !== 'string') {
        const caster = s.source?.sourceType === 'class' ? s.source.sourceId : (s.casterType || s.caster);
        if (caster) acc[s.name] = caster;
      }
      return acc;
    }, {})
  );
  const [pointsLeft, setPointsLeft] = useState({});
  const [activeClass, setActiveClass] = useState(classesInfo[0]?.name || '');
  const [error, setError] = useState(null);
  const [viewSpell, setViewSpell] = useState(null);
  const [preparedLimits, setPreparedLimits] = useState({});
  const [showUpcast, setShowUpcast] = useState(false);
  const [pendingSpell, setPendingSpell] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [schoolFilter, setSchoolFilter] = useState('all');
  const [castingFilter, setCastingFilter] = useState('all');
  const [rangeFilter, setRangeFilter] = useState('all');
  const [traitFilters, setTraitFilters] = useState({
    concentration: false,
    ritual: false,
    cantrip: false,
    known: false,
  });

  const getScaledDamage = useCallback(
    (spell) => {
      let dmg = spell.damage;
      if (spell.scaling) {
        const tiers = Object.keys(spell.scaling)
          .map(Number)
          .sort((a, b) => a - b);
        tiers.forEach((tier) => {
          if (totalLevel >= tier) dmg = spell.scaling[tier];
        });
      }
      return dmg;
    },
    [totalLevel]
  );

  const handleUpcastSelect = async (level, slotType) => {
    if (!pendingSpell) return;
    const diff = level - (pendingSpell.level || 0);
    let extra;
    if (diff > 0 && pendingSpell.higherLevels) {
      const incMatch = pendingSpell.higherLevels.match(/(\d+)d(\d+)/);
      if (incMatch) {
        extra = {
          count: parseInt(incMatch[1], 10),
          sides: parseInt(incMatch[2], 10),
        };
      }
    }
    const damage = getScaledDamage(pendingSpell);
    await onCastSpell?.({
      ...pendingSpell,
      baseLevel: pendingSpell.level,
      slotLevel: level,
      level,
      damage,
      extraDice: extra,
      levelsAbove: diff > 0 ? diff : 0,
      slotType,
      castingTime: pendingSpell.castingTime,
      name: pendingSpell.name,
    });
    setShowUpcast(false);
    setPendingSpell(null);
    handleModalHide();
  };

  useEffect(() => {
    apiFetch('/spells')
      .then((res) => res.json())
      .then((data) => setAllSpells(Object.fromEntries(Object.entries(data || {}).map(([spellId, spell]) => [spellId, { ...spell, spellId: spell.spellId || spellId }]))))
      .catch((err) => setError(err.message));
  }, []);

  useEffect(() => {
    setSelectedSpells(
      (form.spells || []).map((s) => (typeof s === 'string' ? s : s.name))
    );
    setSpellCasters(
      (form.spells || []).reduce((acc, s) => {
        if (s && typeof s !== 'string') {
          const caster = s.source?.sourceType === 'class' ? s.source.sourceId : (s.casterType || s.caster);
          if (caster) acc[s.name] = caster;
        }
        return acc;
      }, {})
    );
  }, [form.spells]);

  useEffect(() => {
    setSelectedLevels(initialLevels);
    setActiveClass(classesInfo[0]?.name || '');
  }, [initialLevels, classesInfo]);

  useEffect(() => {
    const fetchSpellsKnown = async () => {
      const result = {};
      await Promise.all(
        classesInfo.map(async ({ name, level }) => {
          try {
            const res = await apiFetch(
              `/classes/${name.toLowerCase()}/features/${level}`
            );
            if (res.ok) {
              const data = await res.json();
              if (typeof data.preparedSpells === 'number') {
                result[name] = data.preparedSpells;
              }
            }
          } catch (err) {
            setError(err.message);
          }
        })
      );
      setPreparedLimits(result);
    };
    fetchSpellsKnown();
  }, [classesInfo]);

  const spellList = useMemo(() => Object.values(allSpells), [allSpells]);

  const filterOptions = useMemo(() => {
    const schools = new Set();
    const castingTimes = new Set();
    const ranges = new Set();
    spellList.forEach((spell) => {
      if (spell.school) schools.add(spell.school);
      if (spell.castingTime) castingTimes.add(spell.castingTime);
      if (spell.range) ranges.add(spell.range);
    });
    return {
      schools: Array.from(schools).sort(),
      castingTimes: Array.from(castingTimes).sort(),
      ranges: Array.from(ranges).sort(),
    };
  }, [spellList]);

  const selectedSpellSet = useMemo(() => new Set(selectedSpells), [selectedSpells]);
  const wizardBook = getSpellbook(form);

  const filteredSpellsForClass = useCallback(
    (cls) => {
      const term = searchTerm.trim().toLowerCase();
      return spellList.filter((spell) => {
        if (!spellSupportsClass(spell, cls, form)) return false;
        if (spell.level !== Number(selectedLevels[cls])) return false;
        // A Wizard prepares level 1+ spells from the spellbook, once one is saved.
        if (String(cls).toLowerCase() === 'wizard' && spell.level > 0 && wizardBook && !wizardBook.includes(spellSlug(spell.name))) return false;
        if (term) {
          const haystack = `${spell.name || ''} ${spell.school || ''} ${spell.description || ''}`.toLowerCase();
          if (!haystack.includes(term)) return false;
        }
        if (schoolFilter !== 'all' && spell.school !== schoolFilter) return false;
        if (castingFilter !== 'all' && spell.castingTime !== castingFilter) return false;
        if (rangeFilter !== 'all' && spell.range !== rangeFilter) return false;
        const duration = String(spell.duration || '').toLowerCase();
        if (traitFilters.concentration && !duration.includes('concentration')) return false;
        if (traitFilters.ritual && !spell.ritual) return false;
        if (traitFilters.cantrip && spell.level !== 0) return false;
        if (traitFilters.known && !selectedSpellSet.has(spell.name)) return false;
        return true;
      });
    },
    [castingFilter, form, rangeFilter, schoolFilter, searchTerm, selectedLevels, selectedSpellSet, spellList, traitFilters, wizardBook]
  );

  function spellsForClass(cls) {
    return filteredSpellsForClass(cls);
  }

  useEffect(() => {
    const newPoints = {};
    classesInfo.forEach(({ name, level }) => {
      const selectedLevel = Number(selectedLevels[name]);
      const total =
        selectedLevel === 0
          ? getClassSpellLimits(name, level, form).cantrips
          : preparedLimits[name] ?? getClassSpellLimits(name, level).preparedSpells;
      const count = selectedSpells.reduce((sum, spellName) => {
        const info = Object.values(allSpells).find((s) => s.name === spellName);
        return info &&
          (spellCasters[spellName]
            ? normalizeClassName(spellCasters[spellName]) === normalizeClassName(name)
            : spellSupportsClass(info, name, form)) &&
          (selectedLevel === 0 ? info.level === 0 : info.level > 0)
          ? sum + 1
          : sum;
      }, 0);
      newPoints[name] =
        total === Infinity ? Infinity : Math.max(0, total - count);
    });
    setPointsLeft(newPoints);
  }, [form, selectedLevels, selectedSpells, allSpells, classesInfo, preparedLimits, spellCasters]);

  function toggleSpell(name, caster) {
    const isSelected = selectedSpells.includes(name);
    const updatedSpells = isSelected
      ? selectedSpells.filter((s) => s !== name)
      : [...selectedSpells, name];
    const updatedCasters = { ...spellCasters };
    if (isSelected) {
      delete updatedCasters[name];
    } else {
      updatedCasters[name] = caster;
    }
    const previousSpells = selectedSpells, previousCasters = spellCasters;
    setSelectedSpells(updatedSpells);
    setSpellCasters(updatedCasters);
    // A refused change puts the spell back.
    saveSpells(updatedSpells, updatedCasters).then((saved) => {
      if (saved) return;
      setSelectedSpells(previousSpells);
      setSpellCasters(previousCasters);
    });
  }

  const handleModalHide = useCallback(() => {
    if (isDocked) {
      if (typeof onDockClose === 'function') {
        onDockClose();
      }
      return;
    }

    handleClose?.();
  }, [handleClose, isDocked, onDockClose]);

  const castSpell = useCallback(
    async (spell, isSelected) => {
      if (!isSelected) return;
      if (Number(spell.level) > 0) {
        setPendingSpell({ ...spell, sourceId: activeClass });
        setShowUpcast(true);
      } else {
        const damage = getScaledDamage(spell);
        try {
          await onCastSpell?.({
            sourceId: activeClass,
            spellId: spell.spellId,
            level: spell.level,
            damage,
            castingTime: spell.castingTime,
            name: spell.name,
          });
          handleModalHide();
        } catch (error) { setError(error.message); }
      }
    },
    [activeClass, getScaledDamage, handleModalHide, onCastSpell]
  );

  const FilterPanel = ({ cls }) => (
    <aside className="spellbook-sidebar" aria-label="Spell filters">
      <div className="spellbook-filter-group spellbook-filter-group--classes">
        <span className="spellbook-filter-label">Class</span>
        <div className="spellbook-class-list" role="list">
          {classesInfo.map(({ name }) => {
            const key = normalizeToken(name);
            return (
              <button
                type="button"
                key={name}
                className={`spellbook-class-pill${activeClass === name ? ' is-active' : ''}`}
                onClick={() => setActiveClass(name)}
                aria-pressed={activeClass === name}
              >
                <i className={`fa-solid ${CLASS_ICONS[key] || 'fa-book-sparkles'}`} aria-hidden="true" />
                <span>{name}</span>
              </button>
            );
          })}
        </div>
      </div>

      <Form.Group className="spellbook-filter-group">
        <Form.Label htmlFor={`spellLevel-${cls}`} className="spellbook-filter-label">Level</Form.Label>
        <Form.Select
          id={`spellLevel-${cls}`}
          value={selectedLevels[cls]}
          onChange={(e) => setSelectedLevels((prev) => ({ ...prev, [cls]: Number(e.target.value) }))}
          className="spellbook-select"
        >
          {(levelOptions[cls] || []).map((lvl) => (
            <option key={lvl} value={lvl}>{lvl}</option>
          ))}
        </Form.Select>
      </Form.Group>

      <Form.Group className="spellbook-filter-group">
        <Form.Label className="spellbook-filter-label">School</Form.Label>
        <Form.Select value={schoolFilter} onChange={(e) => setSchoolFilter(e.target.value)} className="spellbook-select" aria-label="Spell school">
          <option value="all">All Schools</option>
          {filterOptions.schools.map((school) => <option key={school} value={school}>{school}</option>)}
        </Form.Select>
      </Form.Group>

      <Form.Group className="spellbook-filter-group">
        <Form.Label className="spellbook-filter-label">Casting Time</Form.Label>
        <Form.Select value={castingFilter} onChange={(e) => setCastingFilter(e.target.value)} className="spellbook-select" aria-label="Casting time">
          <option value="all">Any Time</option>
          {filterOptions.castingTimes.map((time) => <option key={time} value={time}>{time}</option>)}
        </Form.Select>
      </Form.Group>

      <Form.Group className="spellbook-filter-group">
        <Form.Label className="spellbook-filter-label">Range</Form.Label>
        <Form.Select value={rangeFilter} onChange={(e) => setRangeFilter(e.target.value)} className="spellbook-select" aria-label="Range">
          <option value="all">Any Range</option>
          {filterOptions.ranges.map((range) => <option key={range} value={range}>{range}</option>)}
        </Form.Select>
      </Form.Group>

      <div className="spellbook-filter-group spellbook-rune-toggles" aria-label="Spell traits">
        {[['concentration', 'Concentration'], ['ritual', 'Ritual'], ['cantrip', 'Cantrip'], ['known', 'Known only']].map(([key, label]) => (
          <button
            type="button"
            key={key}
            className={`spellbook-rune-toggle${traitFilters[key] ? ' is-active' : ''}`}
            onClick={() => setTraitFilters((prev) => ({ ...prev, [key]: !prev[key] }))}
            aria-pressed={traitFilters[key]}
          >
            {label}
          </button>
        ))}
      </div>
    </aside>
  );

  const SpellInspector = ({ spell, cls }) => {
    const activeSpell = spell && spellSupportsClass(spell, cls, form) ? spell : null;
    if (!activeSpell) {
      return <aside className="spellbook-inspector spellbook-inspector--empty">Select a spell to open its illuminated page.</aside>;
    }
    const isSelected = selectedSpellSet.has(activeSpell.name);
    const disableSelection = !isSelected && (pointsLeft[cls] || 0) <= 0;
    return (
      <aside className="spellbook-inspector" aria-label="Selected spell details">
        <div className="spellbook-art"><i className={`fa-solid ${spellIconClass(activeSpell)}`} aria-hidden="true" /></div>
        <Button
          type="button"
          variant="link"
          className="spellbook-inspector-dismiss"
          aria-label="Close selected spell details"
          onClick={() => setViewSpell(null)}
        >
          <i className="fa-solid fa-xmark" aria-hidden="true" />
        </Button>
        <div className="spellbook-inspector-heading">
          <span className="spell-level-badge">Level {activeSpell.level}</span>
          <h3>{activeSpell.name}</h3>
          <p>{[activeSpell.school || 'Unknown School', normalizeClassName(cls) === 'bard' && magicalSecretsLabel(activeSpell)].filter(Boolean).join(' · ')}</p>
        </div>
        <div className="spellbook-stat-grid">
          {['castingTime','range','duration','damage'].map((key) => activeSpell[key] ? <div key={key}><span>{key === 'castingTime' ? 'Casting' : key}</span><strong>{activeSpell[key]}</strong></div> : null)}
          {activeSpell.components?.length ? <div><span>Components</span><strong>{activeSpell.components.join(', ')}</strong></div> : null}
        </div>
        {activeSpell.components?.some(component => String(component).toLowerCase() === 'm') && <p><strong>Material (informational):</strong> {activeSpell.material || 'Material component listed; details are not available in this catalogue.'} Inventory is not required for casting.</p>}
        <p className="spellbook-description">{activeSpell.description || 'No description recorded.'}</p>
        {activeSpell.higherLevels ? <p className="spellbook-higher"><strong>Higher Levels.</strong> {activeSpell.higherLevels}</p> : null}
        <div className="spellbook-inspector-actions">
          <Button className="spellbook-learn-btn" disabled={disableSelection} onClick={() => toggleSpell(activeSpell.name, cls)}>{isSelected ? 'Remove' : 'Learn'}</Button>
          <Button className="spellbook-preview-btn" disabled={!isSelected} onClick={() => castSpell(activeSpell, isSelected)}><i className="fa-solid fa-wand-sparkles" /> Cast</Button>
          {activeSpell.ritual && !isSelected && String(cls).toLowerCase() === 'wizard' && wizardBook?.includes(spellSlug(activeSpell.name)) && <Button variant="secondary" onClick={async () => {
            try { await onCastSpell?.({ featureGrantId: ritualGrantId(spellSlug(activeSpell.name)), name: activeSpell.name, level: activeSpell.level, baseLevel: activeSpell.level, mode: 'ritual', castingTime: activeSpell.castingTime }); handleModalHide(); }
            catch (error) { setError(error.message); }
          }}>Cast as Ritual (+10 minutes)</Button>}
          {activeSpell.ritual && isSelected && <Button variant="secondary" onClick={async () => {
            try { await onCastSpell?.({ name: activeSpell.name, sourceId: cls, level: activeSpell.level, mode: 'ritual' }); handleModalHide(); }
            catch (error) { setError(error.message); }
          }}>Cast as Ritual (+10 minutes)</Button>}
        </div>
      </aside>
    );
  };

  const renderSpellCards = (cls) => {
    const spells = spellsForClass(cls);
    if (!spells.length) {
      return <div className="spellbook-empty">No spells match these runes.</div>;
    }

    return (
      <div className="spell-card-grid spellbook-card-grid">
        {spells.map((spell) => {
          const isSelected = selectedSpellSet.has(spell.name);
          const disableSelection = !isSelected && (pointsLeft[cls] || 0) <= 0;
          const duration = String(spell.duration || '');

          return (
            <article key={spell.name} className={`spell-card spellbook-card${isSelected ? ' is-selected' : ''}`} tabIndex={0} onClick={() => setViewSpell(spell)} onKeyDown={(e) => { if (e.key === 'Enter') setViewSpell(spell); }}>
              <div className="spellbook-card-topline">
                <div className="spellbook-icon"><i className={`fa-solid ${spellIconClass(spell)}`} aria-hidden="true" /></div>
                {normalizeClassName(cls) === 'bard' && magicalSecretsLabel(spell) && <Badge className="spell-level-badge">{magicalSecretsLabel(spell)}</Badge>}
                <Badge className="spell-level-badge">{spell.level === 0 ? 'Cantrip' : `Lv ${spell.level}`}</Badge>
              </div>
              <div className="spell-card-header">
                <Form.Check
                  id={`spell-${cls}-${spell.name}`}
                  type="checkbox"
                  label={spell.name}
                  checked={isSelected}
                  disabled={disableSelection}
                  onClick={(e) => e.stopPropagation()}
                  onChange={() => toggleSpell(spell.name, cls)}
                />
                <div className="spell-card-actions">
                  <Button variant="link" aria-label={`View ${spell.name}`} onClick={(e) => { e.stopPropagation(); setViewSpell(spell); }}><i className="fa-solid fa-eye"></i></Button>
                  <Button variant="link" aria-label={`Cast ${spell.name}`} disabled={!isSelected} className={!isSelected ? 'text-secondary' : ''} onClick={(e) => { e.stopPropagation(); castSpell(spell, isSelected); }}><i className="fa-solid fa-wand-sparkles" /></Button>
                </div>
              </div>
              <div className="spell-card-details">
                <span><strong>School:</strong> {spell.school}</span>
                <span><strong>Casting Time:</strong> {spell.castingTime}</span>
                <span><strong>Range:</strong> {spell.range}</span>
                <span><strong>Duration:</strong> {spell.duration}</span>
              </div>
              <p className="spellbook-card-preview">{summarizeSpell(spell.description)}</p>
              <div className="spellbook-card-traits">
                {duration.toLowerCase().includes('concentration') && <span>Concentration</span>}
                {spell.ritual && <span>Ritual</span>}
              </div>
            </article>
          );
        })}
      </div>
    );
  };

  async function saveSpells(
    spells = selectedSpells,
    casters = spellCasters
  ) {
    try {
      // This persisted compatibility counter always means remaining level-1+ choices,
      // regardless of the currently displayed spell-level filter.
      const currentPoints = classesInfo.reduce((sum, { name, level }) => {
        const total = preparedLimits[name] ?? getClassSpellLimits(name, level).preparedSpells;
        const count = spells.filter((spellName) => {
          const info = Object.values(allSpells).find((spell) => spell.name === spellName);
          const owner = casters[spellName];
          return info?.level > 0 && (owner
            ? normalizeClassName(owner) === normalizeClassName(name)
            : spellSupportsClass(info, name, form));
        }).length;
        return sum + Math.max(0, total - count);
      }, 0);

      const selectedSpellObjects = spells.map((name) => {
        const info = Object.values(allSpells).find((s) => s.name === name) || {};
        const sourceId = normalizeClassName(casters[name]);
        return {
          spellId: info.spellId,
          source: { sourceType: 'class', sourceId },
          name,
          level: info.level || 0,
          damage: info.damage || '',
          castingTime: info.castingTime || '',
          range: info.range || '',
          duration: info.duration || '',
          higherLevels: info.higherLevels,
          scaling: info.scaling,
        };
      });
      let persistedSpells = form.spells || [];
      for (const { name: className } of classesInfo) {
        const classId = normalizeClassName(className);
        const classSpells = selectedSpellObjects.filter((spell) => spell.source.sourceId === classId);
        const res = await apiFetch(`/characters/${params.id}/spell-sources/class/${classId}/spells`, {
          method: 'PUT', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ spells: classSpells, spellPoints: currentPoints }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Failed to save class spell selection');
        const payload = await res.json();
        // Production returns server-issued instance ids and provenance. Keep the
        // legacy fallback only for older compatible servers that omit the list.
        persistedSpells = Array.isArray(payload.spells) ? payload.spells : selectedSpellObjects;
      }
      onSpellsChange?.(persistedSpells, currentPoints);
      return true;
    } catch (err) {
      setError(err.message);
      return false;
    }
  }

  const dialogClassName = useMemo(() => {
    if (!isDocked) {
      return undefined;
    }

    const classes = ['docked-modal'];
    if (dockedSide) {
      classes.push(`docked-modal--${dockedSide}`);
    }
    classes.push('docked-modal--spells');
    return classes.join(' ');
  }, [isDocked, dockedSide]);

  const modalClassName = useMemo(() => {
    const classes = ['dnd-modal', 'modern-modal'];
    if (isDocked) {
      classes.push('docked-modal-container');
    }
    return classes.join(' ');
  }, [isDocked]);

  return (
    <>
      <Modal
        className={modalClassName}
        show={show}
        onHide={handleModalHide}
        size="lg"
        centered={!isDocked}
        backdrop={isDocked ? false : true}
        enforceFocus={!isDocked}
        restoreFocus={!isDocked}
        dialogClassName={dialogClassName}
      >
        <Card className="modern-card">
          <Card.Header className="modal-header">
            <DockControls
              dockedSide={dockedSide}
              onDockChange={onDockChange}
              isDocked={isDocked}
            />
            <Card.Title className="modal-title">Spells</Card.Title>
          </Card.Header>
          <Card.Body className="spellbook-modal-body">
            {error && <div className="text-danger mb-2">{error}</div>}
            {classesInfo.length === 0 ? (
              <div className="text-light">No spellcasting classes available.</div>
            ) : (
              <div className="spellbook-shell">
                <div className="spellbook-hero">
                  <div>
                    <span className="spellbook-kicker">Arcane Library</span>
                    <h2>Spell Selection</h2>
                    <p>Search, filter, learn, and preview your character's magic without leaving the tome.</p>
                  </div>
                  <div className="spellbook-search-wrap">
                    <i className="fa-solid fa-magnifying-glass" aria-hidden="true" />
                    <Form.Control
                      type="search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search the stacks..."
                      aria-label="Search spells"
                      className="spellbook-search"
                    />
                  </div>
                  <div className={`spellbook-points ${(pointsLeft[activeClass] || 0) <= 1 ? 'is-low' : ''}`}>
                    <span>Prepared Spells Left</span>
                    <strong>{pointsLeft[activeClass] === Infinity ? '∞' : pointsLeft[activeClass] || 0}</strong>
                    <small>{activeClass || 'Class'} • Level {selectedLevels[activeClass] ?? '—'}</small>
                  </div>
                </div>

                <Tabs activeKey={activeClass} onSelect={(k) => setActiveClass(k || '')} className="spellbook-tabs mb-3">
                  {classesInfo.map(({ name }) => (
                    <Tab eventKey={name} title={name} key={name}>
                      <div className="spellbook-layout">
                        <FilterPanel cls={name} />
                        <main className="spellbook-library" aria-label={`${name} spell library`}>
                          <div className="spellbook-library-header">
                            <div>
                              <span className="points-label">Points Left:</span>
                              <span className="points-value">{pointsLeft[name] === Infinity ? '∞' : pointsLeft[name] || 0}</span>
                            </div>
                            {spellChangeNote(form, name) && <span className="spellbook-change-note">{spellChangeNote(form, name)}</span>}
                            <span>{spellsForClass(name).length} spells discovered</span>
                          </div>
                          {renderSpellCards(name)}
                        </main>
                        <SpellInspector spell={viewSpell} cls={name} />
                      </div>
                    </Tab>
                  ))}
                </Tabs>
              </div>
            )}
          </Card.Body>
          <Card.Footer className="modal-footer">
            <Button
              className="spellbook-modal-close-btn"
              onClick={handleModalHide}
            >
              Close
            </Button>
          </Card.Footer>
        </Card>
      </Modal>
      <UpcastModal
        show={showUpcast}
        onHide={() => {
          setShowUpcast(false);
          setPendingSpell(null);
        }}
        baseLevel={pendingSpell?.level}
        slots={availableSlots}
        higherLevels={pendingSpell?.higherLevels}
        onSelect={handleUpcastSelect}
      >
        <p>Resolve targeting and spell effects manually.</p>
      </UpcastModal>
    </>
  );
}

